// @flow
import React, { Suspense, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { httpGet } from 'lib/http'
import { isCsv, isDocx, isImage, isXlsx } from 'lib/digital_file/viewer'
import styles from './styles.css'

const XlsxView = React.lazy(() => import('./XlsxView'))
const DocxView = React.lazy(() => import('./DocxView'))

const FileViewer = () => {
  const [params] = useSearchParams()
  const key = params.get('key')
  const name = params.get('name') || 'File'
  const contentType = params.get('content_type')

  const [src, setSrc] = useState<?string>(null)
  const [error, setError] = useState<?string>(null)

  useEffect(() => {
    document.title = name
  }, [name])

  useEffect(() => {
    if (!key) {
      setError('Missing file key.')
      return
    }
    let cancelled = false
    httpGet(`/admin/files/${encodeURIComponent(key)}/preview`)
      .then(({ url }) => {
        if (!cancelled) setSrc(url)
      })
      .catch((err) => {
        if (!cancelled) setError(err && err.message ? err.message : String(err))
      })
    return () => {
      cancelled = true
    }
  }, [key])

  if (error) {
    return <div className={styles.message}>Unable to load file: {error}</div>
  }

  if (!src) {
    return <div className={styles.message}>Loading…</div>
  }

  const fallback = <div className={styles.message}>Loading viewer…</div>

  if (isImage(contentType, name)) {
    return (
      <div className={styles.imageContainer}>
        <img className={styles.image} src={src} alt={name} />
      </div>
    )
  }

  if (isXlsx(contentType, name) || isCsv(contentType, name)) {
    return (
      <Suspense fallback={fallback}>
        <XlsxView src={src} />
      </Suspense>
    )
  }

  if (isDocx(contentType, name)) {
    return (
      <Suspense fallback={fallback}>
        <DocxView src={src} />
      </Suspense>
    )
  }

  return (
    <iframe
      className={styles.frame}
      src={src}
      title={name}
      allow="fullscreen"
    />
  )
}

export default FileViewer
